// Phase 29 — modal launched from DealDetail header that lets the user pick
// other deals to compare against this one, then navigates to
// `/compare?ids=<this_deal>,<picked_ids>`.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Microscope, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useFmt } from "@/lib/useFmt";
import { PropertyDetailLine } from "@/components/property/PropertyMeta";

export default function CompareToModal({ open, onOpenChange, currentDealId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fmt = useFmt();
  const isPro = ["pro", "premium", "enterprise"].includes((user?.subscription_plan || "free").toLowerCase()) || !!user?.is_admin;
  const maxOther = isPro ? 9 : 1; // total selection = current + others

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(() => new Set());

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/deals")
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        setDeals(items.filter((d) => d.id !== currentDealId));
      })
      .catch(() => setDeals([]))
      .finally(() => setLoading(false));
  }, [open, currentDealId]);

  const filtered = deals.filter((d) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (d.property?.address || "").toLowerCase().includes(q) ||
      (d.property?.city || "").toLowerCase().includes(q) ||
      (d.deal_type || "").toLowerCase().includes(q)
    );
  });

  const toggle = (id) => {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else {
        if (next.size >= maxOther) return next;
        next.add(id);
      }
      return next;
    });
  };

  const handleGo = () => {
    if (picked.size === 0) return;
    const ids = [currentDealId, ...Array.from(picked)].join(",");
    onOpenChange(false);
    navigate(`/compare?ids=${ids}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="compare-to-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Microscope className="h-4 w-4 text-indigo-600" /> Compare this deal to…
          </DialogTitle>
          <DialogDescription>
            Pick {isPro ? "up to 9" : "1"} other deal{isPro ? "s" : ""} to open the full side-by-side comparison.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            placeholder="Filter by address, city, or strategy…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="compare-to-modal-search"
          />
          <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
            {loading ? (
              <div className="flex items-center gap-2 p-4 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading your deals…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-sm text-slate-400" data-testid="compare-to-modal-empty">
                No other deals to compare. Run another analysis first.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {filtered.map((d) => {
                  const isPicked = picked.has(d.id);
                  return (
                    <li
                      key={d.id}
                      className={`flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-slate-50 ${
                        isPicked ? "bg-indigo-50" : ""
                      }`}
                      onClick={() => toggle(d.id)}
                      data-testid={`compare-to-modal-row-${d.id}`}
                    >
                      <input
                        type="checkbox"
                        readOnly
                        checked={isPicked}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {d.property?.address || "(no address)"}
                        </p>
                        {/* Dense detail line — drops sqft/rent to fit narrow
                            modal column. Score chip kept inline for ranking. */}
                        <div className="flex items-baseline gap-2">
                          <PropertyDetailLine d={d} fmt={fmt} dense />
                          {Number.isFinite(Number(d.deal_score)) && (
                            <span className="text-[11px] text-slate-400 whitespace-nowrap">
                              · score {Math.round(d.deal_score || 0)}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500" data-testid="compare-to-modal-count">
              {picked.size} of {maxOther} selected
            </span>
            {!isPro && (
              <span className="text-amber-700">Free tier · upgrade to Pro to compare up to 10</span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="compare-to-modal-cancel">
            <X className="me-1 h-3.5 w-3.5" /> Cancel
          </Button>
          <Button onClick={handleGo} disabled={picked.size === 0} data-testid="compare-to-modal-go">
            <Microscope className="me-1 h-3.5 w-3.5" /> Open comparison
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
